<script lang="ts">
	import { onMount } from "svelte";
	import { localeRound } from "#lib/utils/math.js";
	import { createCssColorProbe, type CssColorProbe } from "#lib/utils/cssColor.js";
	import { norwayHourRange } from "#lib/utils/norwayHour.js";
	import type { Dag } from "#lib/types/spotPriser.js";

	type Props = {
		day: Dag;
		/** Hour to mark as "now", as epoch ms from `hourStartMs`. `null`
		 *  disables it (e.g. when viewing yesterday or tomorrow). */
		highlightHourStart?: number | null;
		/** When true, the solid line shows the price WITH strømstøtte applied
		 *  and a dashed reference line shows the without-strømstøtte price. */
		showStromstotte?: boolean;
		/** Render a real y-axis (line, ticks, labels, split lines) on the left
		 *  edge — intended for landscape / tablet+ widths. Below the landscape
		 *  breakpoint the parent keeps this false and the chart relies on the
		 *  two markLines (top + 0 øre) for orientation. */
		showYAxis?: boolean;
	};
	let {
		day,
		highlightHourStart = null,
		showStromstotte = false,
		showYAxis = false,
	}: Props = $props();

	let chartEl: HTMLDivElement | undefined = $state();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let chart: any = $state(undefined);
	// `graphic` in $state so the $effect below sees it reactively and
	// runs buildOptions() synchronously (tracking `day` reads).
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	let graphic: any = $state(undefined);
	let hoverIndex: number | null = $state(null);
	// Pixels the tooltip overflows above the chart's top edge — used to
	// reserve padding-top on the wrapper so the tooltip doesn't clip.
	let topOverflow = $state(24);

	// CSS-var resolution at runtime: ECharts hands colors to canvas, whose
	// color parser in Safari can't evaluate `light-dark()` / nested modern
	// color functions. `createCssColorProbe()` resolves the spec via a
	// hidden element so we only pass plain `rgb()`/`rgba()` to ECharts.
	let colorProbe: CssColorProbe | undefined;

	const COLORS = {
		naust: "#db2600",
		uer: "#ff6c00",
		skog: "#26b41b",
	};

	const GRADIENT_STOPS = [
		{ offset: 0.125, color: COLORS.naust },
		{ offset: 0.375, color: COLORS.uer },
		{ offset: 0.825, color: COLORS.skog },
	];

	// Solid line: with-strømstøtte price when toggle is on (falls back to
	// regular where strømstøtte doesn't apply — NO4, low-price hours).
	const mainPrices = $derived(
		day.priser.map((p) => (showStromstotte ? (p.hasStromstottePris ?? p.pris) : p.pris))
	);

	// Trailing duplicate so the last step extends to the right edge.
	const chartData = $derived.by(() => {
		const data = mainPrices;
		return data.length > 0 ? [...data, data[data.length - 1]] : [];
	});

	// Dashed reference: without-strømstøtte at hours where it differs.
	// `differs[i-1] || differs[i+1]` keeps boundary values so the dashed
	// segment connects visually instead of starting in mid-air.
	const dashedData = $derived.by(() => {
		if (!showStromstotte) return [];
		const without = day.priser.map((p) => p.pris);
		const differs = without.map((v, i) => v !== mainPrices[i]);
		if (!differs.some(Boolean)) return [];
		const masked: (number | null)[] = without.map((v, i) => {
			if (differs[i]) return v;
			if (differs[i - 1] || differs[i + 1]) return v;
			return null;
		});
		return [...masked, masked[masked.length - 1]];
	});

	// "kl." prefix on the first label is injected by axisLabel rich-text
	// so it can be styled bold independently of the digits.
	const xLabels = $derived.by(() => {
		const labels = day.priser.map((p) => p.tidspunkt.slice(11, 13));
		return labels.length > 0 ? [...labels, ""] : [];
	});

	const yMax = $derived(day.priser.reduce((acc, p) => Math.max(acc, p.pris), 0));
	const topLabelValue = $derived(Math.round(yMax * 1.05));
	const chartMax = $derived(topLabelValue * 1.05);
	const chartMinRaw = $derived(Math.round(Math.min(0, ...chartData)));
	const chartMin = $derived(chartMinRaw < 0 ? chartMinRaw * 1.15 : undefined);

	const currentHourIndex = $derived(
		highlightHourStart == null
			? -1
			: day.priser.findIndex(
					(p) => p.validOnUtc != null && Date.parse(p.validOnUtc) === highlightHourStart
				)
	);

	// Hover wins; otherwise highlight the current hour. -1 = nothing.
	const activeIndex = $derived(hoverIndex ?? currentHourIndex);

	// Y-axis label gutter; mirrored in the bucket-hit-test in onMount.
	const Y_AXIS_GUTTER = 44;
	const gridLeft = $derived(showYAxis ? Y_AXIS_GUTTER : 0);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const buildOptions = (graphic: any) => {
		const gradient = new graphic.LinearGradient(0, 0, 0, 1, GRADIENT_STOPS);
		const resolve = colorProbe?.resolve ?? ((_s: string, fb: string) => fb);
		const lysning = resolve("var(--lysning)", "#0a66ff");
		const labelColor = resolve("var(--fg)", "#000");
		// Axis + gridlines derive from --fg so they auto-tint per theme.
		// Same hue source as the info-table card border at `/.2` alpha —
		// chart frame and surrounding card edges read as one family.
		// Resolved through the probe so the relative-color expression is
		// reduced to plain rgba() before reaching canvas (Safari fix).
		const axisColor = resolve("hsl(from var(--fg) h s l / 0.5)", "rgba(0,0,0,0.5)");
		const gridLineColor = resolve("hsl(from var(--fg) h s l / 0.2)", "rgba(0,0,0,0.2)");
		// Halo behind mobile markLine value labels — tracks --bg so it
		// blends with the chart background under light/dark themes.
		const labelBorderColor = resolve("var(--bg)", "#ffffff");

		return {
			textStyle: { fontSize: 10 },
			grid: {
				left: gridLeft,
				right: 0,
				bottom: "15%",
				top: "5%",
				containLabel: false,
			},
			xAxis: {
				type: "category",
				data: xLabels,
				boundaryGap: false,
				axisLine: { onZero: false, lineStyle: { color: axisColor } },
				axisTick: { show: true, inside: false },
				axisLabel: {
					align: "left",
					fontSize: 10,
					color: labelColor,
					// Rich-text formatter splits the first label into a bold
					// "kl." prefix + the hour digits. All other labels render as
					// plain hour strings; the trailing duplicate end-point is
					// hidden by returning an empty string.
					formatter: (value: string, index: number) => {
						if (!value) return "";
						return index === 0 ? `{kl|kl.} ${value}` : value;
					},
					rich: {
						kl: { fontWeight: "bold", color: labelColor },
					},
				},
			},
			yAxis: {
				type: "value",
				max: chartMax,
				min: chartMin,
				// "øre" sits at the top of the axis (its own line above the
				// topmost tick value). The tooltip is opaque (see `tooltip.
				// backgroundColor` below) so this canvas-rendered text doesn't
				// bleed through the tooltip when they overlap.
				name: showYAxis ? "øre" : "",
				nameLocation: "end",
				nameTextStyle: {
					fontSize: 10,
					align: "left",
					padding: [0, 0, 0, -8],
					fontWeight: "bold",
					color: labelColor,
				},
				nameGap: 8,
				axisLine: { show: showYAxis, lineStyle: { color: axisColor } },
				axisTick: { show: showYAxis },
				splitLine: { show: showYAxis, lineStyle: { color: gridLineColor } },
				axisLabel: {
					show: showYAxis,
					fontSize: 10,
					color: labelColor,
					formatter: (v: number) => localeRound(v, { decimals: 0 }),
				},
			},
			series: [
				// Main series first so `seriesIndex: 0` always targets a
				// non-null line for `dispatchAction showTip`. Visual stacking
				// is governed by `z` — dashed (z: 1) renders behind main
				// (z: 2) regardless of array order.
				{
					id: "main",
					type: "line",
					z: 2,
					data: chartData,
					step: "end",
					symbol: "none",
					animation: false,
					areaStyle: { color: gradient, opacity: 0.125 },
					lineStyle: { color: gradient, opacity: 1 },
					markPoint: {
						silent: true,
						animation: false,
						symbol: "circle",
						symbolSize: 7,
						label: { show: false },
						data:
							currentHourIndex !== -1
								? [
										{
											name: "Now",
											coord: [currentHourIndex, mainPrices[currentHourIndex]],
											// Centre the marker in the hour bucket. ECharts
											// category axes snap fractional coords to the nearest
											// tick, so we use a pixel offset (half a tick-width).
											symbolOffset: (() => {
												const n = day.priser.length;
												if (!chartEl || n === 0) return [0, 0];
												const plotWidth = chartEl.clientWidth - gridLeft;
												return [plotWidth / n / 2, 0];
											})(),
											itemStyle: { color: lysning },
										},
									]
								: [],
					},
					markArea: {
						silent: true,
						animation: false,
						label: { show: false },
						itemStyle: { color: lysning, opacity: 0.125 },
						data: activeIndex >= 0 ? [[{ xAxis: activeIndex }, { xAxis: activeIndex + 1 }]] : [],
					},
					// Landscape: y-axis carries the values; no markLines.
					// Mobile: two markLines are the only reference labels
					// (top + 0 øre).
					markLine: {
						silent: true,
						animation: false,
						symbol: "none",
						data: showYAxis
							? []
							: [
									{
										yAxis: topLabelValue,
										lineStyle: { color: gridLineColor, type: "solid", width: 1 },
										label: {
											show: true,
											position: "insideStartBottom",
											fontSize: 10,
											offset: [-5, -2],
											formatter: `${topLabelValue} øre`,
											color: labelColor,
											textBorderColor: labelBorderColor,
											textBorderWidth: 3,
										},
									},
									{
										yAxis: 0,
										lineStyle: { color: gridLineColor, type: "solid", width: 1 },
										label: {
											show: true,
											position: "insideStartTop",
											fontSize: 10,
											offset: [-5, 4],
											formatter: "0 øre",
											color: labelColor,
											textBorderColor: labelBorderColor,
											textBorderWidth: 3,
										},
									},
								],
					},
				},
				// Dashed without-strømstøtte reference. After main so
				// `seriesIndex: 0` stays bound to main; lower z renders behind.
				...(dashedData.length > 0
					? [
							{
								id: "stromstotte-dashed",
								type: "line" as const,
								z: 1,
								data: dashedData,
								step: "end" as const,
								symbol: "none",
								animation: false,
								lineStyle: { color: gradient, type: "dashed" as const },
							},
						]
					: []),
			],
			tooltip: {
				trigger: "axis",
				// `none` disables ECharts' own show/hide so the tooltip
				// stays at the last touched bucket (sticky on side-exit).
				triggerOn: "none",
				axisPointer: { type: "none" },
				textStyle: {
					fontFamily: "var(--font-family-body)",
					fontSize: 13,
					color: "var(--fg)",
				},
				// Opaque so canvas-rendered labels behind the tooltip
				// (yAxis.name "øre") don't bleed through.
				backgroundColor: "color-mix(in oklab, var(--lysning) 12.5%, white)",
				// 50%-saturation lysning border so the edge stays visible
				// against the markArea band (same 12.5% lysning bg).
				borderColor: "color-mix(in oklab, var(--lysning) 50%, transparent)",
				borderWidth: 1,
				borderRadius: 4,
				extraCssText: "box-shadow: none;",
				position: (
					point: [number, number],
					_params: unknown,
					_dom: HTMLElement,
					_rect: unknown,
					size: { contentSize: [number, number]; viewSize: [number, number] }
				) => {
					// `topOverflow` reserves padding-top on the wrapper for
					// the part of the tooltip above the chart. Deferred via
					// queueMicrotask because this callback runs during
					// ECharts' synchronous render — inline state writes
					// raced with that render on first show after navigation.
					const nextOverflow = Math.max(0, size.contentSize[1] - size.viewSize[1] * 0.05);
					if (nextOverflow !== topOverflow) {
						queueMicrotask(() => {
							topOverflow = nextOverflow;
						});
					}
					const numBuckets = day.priser.length;
					const bucketWidth = numBuckets > 0 ? size.viewSize[0] / numBuckets : 0;
					const xCenter = point[0] + bucketWidth / 2;
					return [
						Math.max(
							0,
							Math.min(size.viewSize[0] - size.contentSize[0], xCenter - size.contentSize[0] / 2)
						),
						-size.contentSize[1] + size.viewSize[1] * 0.05,
					];
				},
				formatter: (params: { dataIndex: number; axisValue: string }[]) => {
					// Guard against stale indexes from a prior `day` after data swap.
					const i = params[0]?.dataIndex;
					if (i == null || i < 0 || i >= day.priser.length) return "";
					const p = day.priser[i];
					if (!p) return "";
					const { start: startHour, end: endHour } = norwayHourRange(p.validOnUtc);
					const pris = localeRound(p.pris);
					const value =
						p.hasStromstottePris != null
							? `<strong>${pris} øre/kWh</strong> (m/strømstøtte: ${localeRound(p.hasStromstottePris)})`
							: `<strong>${pris} øre/kWh</strong>`;
					return `kl. ${startHour}:00–${endHour}:00<br/>${value}`;
				},
			},
		};
	};

	// Sync onMount wrapper + async IIFE inside — Svelte 5's types reject
	// returning a cleanup from an async callback, even though it works.
	onMount(() => {
		let cleanup: (() => void) | undefined;

		void (async () => {
			const echarts = await import("echarts");
			if (!chartEl) return;
			colorProbe = createCssColorProbe();
			graphic = echarts.graphic;
			chart = echarts.init(chartEl);

			// Re-run setOption on resize so the markPoint's pixel-based
			// symbolOffset (chartEl.clientWidth-dependent) gets recomputed.
			const onResize = () => {
				chart?.resize();
				if (chart && graphic) chart.setOption(buildOptions(graphic), { replaceMerge: ["series"] });
			};
			window.addEventListener("resize", onResize);

			// Track bucket from raw cursor X rather than ECharts' dataIndex,
			// which snaps to the nearest tick — that makes the highlight
			// jump at tick midpoints instead of at bucket boundaries.
			const zr = chart.getZr();
			const updateBucket = (offsetX: number) => {
				if (!chartEl) return;
				const numBuckets = day.priser.length;
				if (numBuckets === 0) return;
				const plotWidth = chartEl.clientWidth - gridLeft;
				if (plotWidth <= 0) return;
				const bucketWidth = plotWidth / numBuckets;
				const i = Math.floor((offsetX - gridLeft) / bucketWidth);
				// Sticky on side exits: ZRender keeps firing mousemove in the
				// y-axis gutter / past the right edge. Returning early keeps
				// hoverIndex at the last valid bucket — matches top/bottom
				// exits, which don't fire mousemove at all.
				if (i < 0 || i >= numBuckets) return;
				hoverIndex = i;
			};
			const onMousemove = (event: { offsetX: number }) => updateBucket(event.offsetX);
			zr.on("mousemove", onMousemove);

			cleanup = () => {
				window.removeEventListener("resize", onResize);
				zr.off("mousemove", onMousemove);
				chart?.dispose();
				colorProbe?.dispose();
				colorProbe = undefined;
			};
		})();

		return () => cleanup?.();
	});

	// Reset sticky hover on data change (date/area navigation) so it
	// doesn't leak between unrelated charts. Declared BEFORE the
	// setOption effect so dispatchAction below sees the cleared value.
	$effect(() => {
		void day;
		hoverIndex = null;
	});

	// One-shot, not $state (so writing it doesn't retrigger the effect).
	let preMeasured = false;

	// `replaceMerge: ['series']` swaps the whole series array (the dashed
	// line conditionally appears/disappears) but merges everything else —
	// much faster than notMerge=true, which made toggles feel laggy.
	$effect(() => {
		if (!chart || !graphic) return;
		chart.setOption(buildOptions(graphic), { replaceMerge: ["series"] });

		// Pre-measure tooltip dimensions on first render so `topOverflow`
		// holds the real value before first hover. Without it, views
		// without a current-hour anchor (i-går / i-morgen) jump down on
		// first interaction. show/hide pair fires the position callback
		// without leaving a tooltip visible.
		if (!preMeasured && day.priser.length > 0) {
			chart.dispatchAction({ type: "showTip", seriesIndex: 0, dataIndex: 0 });
			chart.dispatchAction({ type: "hideTip" });
			preMeasured = true;
		}

		// Drive tooltip from activeIndex so it tracks the shadow exactly.
		// `seriesIndex: 0` always targets main (never the dashed series,
		// which has null hours that `showTip` refuses to render).
		if (activeIndex >= 0 && activeIndex < day.priser.length) {
			chart.dispatchAction({
				type: "showTip",
				seriesIndex: 0,
				dataIndex: activeIndex,
			});
		} else {
			chart.dispatchAction({ type: "hideTip" });
		}
	});
</script>

<!-- Reserve room above the chart for the tooltip's overflow above the
     top edge (the portion that doesn't overlap grid.top = 5%). -->
<div style="padding-top: {topOverflow + 4}px">
	<div bind:this={chartEl} class="w-full aspect-[5/2] tablet:aspect-video"></div>
</div>
